import { Link, useParams } from "react-router-dom";
import { BannerVistaEjemplo } from "./components/BannerVistaEjemplo";
import { EtapaBrief } from "./components/EtapaBrief";
import { CopyDeCanal } from "./components/CopyDeCanal";
import {
  ArmadoContenido,
  AssetsContenido,
  DireccionCreativaContenido,
  EstrategiaContenido,
  GuionContenido,
  PlanDePlanosContenido,
} from "./components/ContenidoEtapasBrief";
import { BRIEF_EJEMPLO, PROPUESTAS_EJEMPLO } from "./datos/ejemplo";
import type { Canal, FormatoPieza, NombreEtapa } from "./tipos";

/**
 * Detalle de una pieza: el brief que arman los agentes, etapa por etapa, tal
 * como se va produciendo. En el Bloque A solo existe `BRIEF_EJEMPLO`; un
 * `:piezaId` que no sea el suyo cae en el vacío honesto.
 */

// `Record<NombreEtapa, string>` en vez de escribir el título suelto en cada
// bloque: si mañana se agrega una etapa al contrato y acá no se actualiza,
// TypeScript lo marca en vez de dejar un título faltante en silencio.
const TITULOS_ETAPA: Record<NombreEtapa, string> = {
  estrategia: "Estrategia",
  direccion_creativa: "Dirección creativa",
  guion: "Copy y guion",
  plan_de_planos: "Plan de planos",
  assets: "Assets",
  // La CLAVE `armado` es contrato del brief y no cambia; lo que se renombró es
  // el texto visible, para que coincida con el nodo del mapa
  // (ADR-2026-08-16-mapa-anillo-uniforme-por-posicion).
  armado: "Creación de contenido",
};

const ETIQUETAS_AGENTE: Record<string, string> = {
  estratega: "Estratega",
  director_creativo: "Director creativo",
  director_ia: "Director IA",
  armado: "Creación de contenido",
  copy_de_canal: "Copy de canal",
};

function formatAgente(producidoPor: string): string {
  return ETIQUETAS_AGENTE[producidoPor] ?? producidoPor.replace(/_/g, " ");
}

// La coordenada es la celda de la matriz (persona × ángulo × formato ×
// canal) a la que se le suma la métrica cuando vuelva: sin ella, la pieza
// no dice para quién ni para qué canal es. Va en píldoras junto a la línea
// de versión, no adentro de ninguna de las 6 etapas — es un dato de la
// pieza entera, no de una etapa puntual.
const ETIQUETAS_FORMATO: Record<FormatoPieza, string> = {
  reel_9x16: "Reel 9:16",
  feed_1x1: "Feed 1:1",
  feed_4x5: "Feed 4:5",
  carrusel_4x5: "Carrusel 4:5",
};

const ETIQUETAS_CANAL: Record<Canal, string> = {
  instagram: "Instagram",
  meta_ads: "Meta Ads",
};

const claseChipCoordenada =
  "rounded-full bg-[#02062B]/[0.05] py-1 px-2.5 text-[11px] font-medium text-[#02062B]/[0.65]";

export const PiezaPage = () => {
  const { piezaId } = useParams<{ piezaId: string }>();
  const brief = piezaId === BRIEF_EJEMPLO.pieza_id ? BRIEF_EJEMPLO : null;

  if (!brief) {
    return (
      <div className="p-8">
        <BannerVistaEjemplo />
        <div className="mt-8 flex flex-col items-start gap-4 rounded-2xl border border-dashed border-[#E2E4EA] p-8">
          <div>
            <h1 className="text-[18px] font-semibold text-brand-black">
              Esta pieza todavía no tiene brief.
            </h1>
            <p className="mt-1 text-[13px] text-[#02062B]/[0.55]">
              Probá desde la lista de propuestas del Plan.
            </p>
          </div>
          <Link
            to="/estudio-marketing/plan"
            className="rounded-full bg-brand-primary py-2 px-4 text-[12px] font-medium text-white"
          >
            Volver al plan
          </Link>
        </div>
      </div>
    );
  }

  const propuesta = PROPUESTAS_EJEMPLO.find((item) => item.pieza_id === brief.pieza_id);

  return (
    <div className="p-8">
      <BannerVistaEjemplo />

      <div className="mt-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-semibold text-brand-black">
            {propuesta?.titulo ?? "Detalle de pieza"}
          </h1>
          <p className="mt-1 text-[13px] text-[#02062B]/[0.55]">
            Brief v{brief.version} · lo editó {brief.la_escribio}
          </p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            <span className={claseChipCoordenada}>{brief.coordenada.persona.replace(/_/g, " ")}</span>
            <span className={claseChipCoordenada}>{brief.coordenada.angulo.replace(/_/g, " ")}</span>
            <span className={claseChipCoordenada}>{ETIQUETAS_FORMATO[brief.coordenada.formato]}</span>
            <span className={claseChipCoordenada}>{ETIQUETAS_CANAL[brief.coordenada.canal]}</span>
          </div>
        </div>
        <Link to="/estudio-marketing/plan" className="text-[12px] font-medium text-[#1644EB]">
          ‹ Volver al plan
        </Link>
      </div>

      <div className="mt-6 flex flex-col gap-4">
        <EtapaBrief
          numero={1}
          titulo={TITULOS_ETAPA.estrategia}
          agente={formatAgente(brief.estrategia._meta.producido_por)}
          meta={brief.estrategia._meta}
        >
          <EstrategiaContenido estrategia={brief.estrategia} />
        </EtapaBrief>

        <EtapaBrief
          numero={2}
          titulo={TITULOS_ETAPA.direccion_creativa}
          agente={formatAgente(brief.direccion_creativa._meta.producido_por)}
          meta={brief.direccion_creativa._meta}
        >
          <DireccionCreativaContenido direccion={brief.direccion_creativa} />
        </EtapaBrief>

        <EtapaBrief
          numero={3}
          titulo={TITULOS_ETAPA.guion}
          agente={formatAgente(brief.guion._meta.producido_por)}
          meta={brief.guion._meta}
        >
          <GuionContenido guion={brief.guion} />
        </EtapaBrief>

        <EtapaBrief
          numero={4}
          titulo={TITULOS_ETAPA.plan_de_planos}
          agente={formatAgente(brief.plan_de_planos._meta.producido_por)}
          meta={brief.plan_de_planos._meta}
        >
          <PlanDePlanosContenido planDePlanos={brief.plan_de_planos} />
        </EtapaBrief>

        <EtapaBrief
          numero={5}
          titulo={TITULOS_ETAPA.assets}
          agente={formatAgente(brief.assets._meta.producido_por)}
          meta={brief.assets._meta}
        >
          <AssetsContenido assets={brief.assets} />
        </EtapaBrief>

        <EtapaBrief
          numero={6}
          titulo={TITULOS_ETAPA.armado}
          agente={formatAgente(brief.armado._meta.producido_por)}
          meta={brief.armado._meta}
        >
          <ArmadoContenido armado={brief.armado} />
        </EtapaBrief>
      </div>

      <div className="mt-6">
        <CopyDeCanal
          copy={brief.copy_de_canal}
          agente={formatAgente(brief.copy_de_canal._meta.producido_por)}
        />
      </div>
    </div>
  );
};

export default PiezaPage;
